import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { Ago, EmptyBlock, ErrorBlock, FetchingDot, LoadingBlock, SectionCard } from "@/monitoring/components/primitives";
import { formatStamp } from "@/monitoring/format";
import type { SettingsHistoryItem } from "../types";
import type { SettingText } from "../use-setting-text";

const FOLDED = 6;

/** Saved changes, newest first: who, when, and every key as old -> new. */
export function HistorySection({
  items,
  loading,
  fetching,
  error,
  onRetry,
  text,
}: {
  items: SettingsHistoryItem[] | undefined;
  loading: boolean;
  fetching: boolean;
  error: string | null;
  onRetry: () => void;
  text: SettingText;
}) {
  const { t } = useLocalization();
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items ?? [] : (items ?? []).slice(0, FOLDED);

  return (
    <SectionCard
      id="history"
      title={t("evalSettings.history.title")}
      description={t("evalSettings.history.description")}
      actions={<FetchingDot active={fetching && !loading} />}
    >
      {error && !items ? (
        <ErrorBlock message={error} onRetry={onRetry} />
      ) : loading && !items ? (
        <LoadingBlock height={120} />
      ) : !items?.length ? (
        <EmptyBlock>{t("evalSettings.history.empty")}</EmptyBlock>
      ) : (
        <>
          <ol className="flex flex-col">
            {shown.map((item) => {
              const ts = Date.parse(item.at);
              return (
                <li
                  key={`${item.version}-${item.at}`}
                  className="flex min-w-0 flex-col gap-1.5 border-b border-border/50 py-3 first:pt-0 last:border-b-0 last:pb-0 md:flex-row md:gap-6"
                >
                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs text-muted-foreground md:w-60 md:shrink-0 md:flex-col md:gap-0.5">
                    <span className="font-mono font-medium text-foreground">
                      {t("evalSettings.history.version", { version: item.version })}
                    </span>
                    <span className="min-w-0 break-all">{item.by?.email ?? t("evalSettings.unknownUser")}</span>
                    <span className="tabular-nums">
                      <Ago ts={Number.isFinite(ts) ? ts : null} /> · {formatStamp(Number.isFinite(ts) ? ts : null)}
                    </span>
                  </div>
                  <ul className="flex min-w-0 flex-1 flex-col gap-1">
                    {item.changes.map((change) => (
                      <li key={change.key} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                        <span className="text-foreground/90" title={change.key}>
                          {text.label(change.key)}
                        </span>
                        <span className="inline-flex items-center gap-1.5 tabular-nums">
                          <span className="text-muted-foreground">{text.value(change.key, change.old)}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden />
                          <span className="font-medium">{text.value(change.key, change.new)}</span>
                        </span>
                        {change.reset && (
                          <span className="inline-flex h-5 items-center rounded-md border px-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t("evalSettings.history.reset")}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ol>
          {items.length > FOLDED && (
            <Button variant="ghost" size="sm" className="mt-2 h-8 px-2 text-xs" onClick={() => setExpanded((v) => !v)}>
              {expanded ? t("evalSettings.history.showLess") : t("evalSettings.history.showAll", { count: items.length })}
            </Button>
          )}
        </>
      )}
    </SectionCard>
  );
}
